import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingBag, Leaf, Coffee, Apple, Package, Pill, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NutritionShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { user } = useAuth();
  const { addToCart } = useCart();

  const categories = [
    { id: 'all', name: 'All Products', icon: Leaf },
    { id: 'teas_supplements', name: 'Teas & Supplements', icon: Pill },
    { id: 'healthy_drinks', name: 'Healthy Drinks', icon: Coffee },
    { id: 'convenient_nutrition', name: 'Convenient Nutrition', icon: Apple },
    { id: 'meal_alternatives', name: 'Meal Alternatives', icon: UtensilsCrossed },
    { id: 'meal_kits', name: 'Meal Kits & Accessories', icon: Package },
  ];

  // Sample nutrition products data
  const nutritionProducts = [
    // Teas & Supplements
    { id: 'nut-1', name: 'Metabolism Boost Tea', description: 'Premium green tea blend with ginger and turmeric to boost metabolism naturally', price: 24.99, category: 'teas_supplements', image_url: 'https://images.pexels.com/photos/1417945/pexels-photo-1417945.jpeg', in_stock: true },
    { id: 'nut-2', name: 'Daily Multivitamin Complex', description: 'Complete daily multivitamin with essential nutrients for optimal health', price: 34.99, category: 'teas_supplements', image_url: 'https://images.pexels.com/photos/3683074/pexels-photo-3683074.jpeg', in_stock: true },
    { id: 'nut-3', name: 'Antioxidant Super Blend', description: 'Powerful antioxidant supplement with acai, goji, and pomegranate extracts', price: 39.99, category: 'teas_supplements', image_url: 'https://images.pexels.com/photos/4021983/pexels-photo-4021983.jpeg', in_stock: true },
    { id: 'nut-4', name: 'Calming Chamomile Tea', description: 'Organic chamomile tea for relaxation and better sleep', price: 18.99, category: 'teas_supplements', image_url: 'https://images.pexels.com/photos/1638280/pexels-photo-1638280.jpeg', in_stock: true },
    
    // Healthy Drinks
    { id: 'nut-5', name: 'Green Detox Smoothie Mix', description: 'Nutrient-packed smoothie mix with spirulina, kale, and spinach', price: 29.99, category: 'healthy_drinks', image_url: 'https://images.pexels.com/photos/1346347/pexels-photo-1346347.jpeg', in_stock: true },
    { id: 'nut-6', name: 'Berry Wellness Juice Concentrate', description: 'Concentrated berry juice blend rich in vitamins C and antioxidants', price: 22.99, category: 'healthy_drinks', image_url: 'https://images.pexels.com/photos/1328887/pexels-photo-1328887.jpeg', in_stock: true },
    { id: 'nut-7', name: 'Collagen Beauty Elixir', description: 'Marine collagen drink for skin, hair, and nail health', price: 44.99, category: 'healthy_drinks', image_url: 'https://images.pexels.com/photos/4158/apple-iphone-smartphone-desk.jpg', in_stock: true },
    
    // Convenient Nutrition
    { id: 'nut-8', name: 'Overnight Oats Starter Kit', description: 'Quick-prep breakfast oats with chia seeds and dried fruits', price: 19.99, category: 'convenient_nutrition', image_url: 'https://images.pexels.com/photos/1640770/pexels-photo-1640770.jpeg', in_stock: true },
    { id: 'nut-9', name: 'Fortified Grain Cereal', description: 'High-fiber cereal fortified with vitamins and minerals', price: 14.99, category: 'convenient_nutrition', image_url: 'https://images.pexels.com/photos/135525/pexels-photo-135525.jpeg', in_stock: true },
    { id: 'nut-10', name: 'Healthy Fruit Snack Pack', description: 'Assorted dried fruit snacks - no added sugar, all natural', price: 16.99, category: 'convenient_nutrition', image_url: 'https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg', in_stock: true },
    { id: 'nut-11', name: 'Late Night Healthy Bites', description: 'Low-calorie snacks perfect for late-night cravings', price: 12.99, category: 'convenient_nutrition', image_url: 'https://images.pexels.com/photos/1099680/pexels-photo-1099680.jpeg', in_stock: true },
    
    // Meal Alternatives
    { id: 'nut-12', name: 'Protein Power Bar Box', description: 'High-protein, low-sugar bars - 12 pack variety', price: 32.99, category: 'meal_alternatives', image_url: 'https://images.pexels.com/photos/3026808/pexels-photo-3026808.jpeg', in_stock: true },
    { id: 'nut-13', name: 'Meal Replacement Shake', description: 'Complete nutrition shake with 25g protein per serving', price: 49.99, category: 'meal_alternatives', image_url: 'https://images.pexels.com/photos/3735466/pexels-photo-3735466.jpeg', in_stock: true },
    { id: 'nut-14', name: 'Keto Energy Bites', description: 'Low-carb, high-fat snack bites for ketogenic diets', price: 24.99, category: 'meal_alternatives', image_url: 'https://images.pexels.com/photos/4397899/pexels-photo-4397899.jpeg', in_stock: true },
    
    // Meal Kits & Accessories
    { id: 'nut-15', name: 'Premium Insulated Lunch Box', description: 'Stainless steel insulated container keeps food fresh for 8+ hours', price: 45.99, category: 'meal_kits', image_url: 'https://images.pexels.com/photos/5591854/pexels-photo-5591854.jpeg', in_stock: true },
    { id: 'nut-16', name: 'Weekly Pill Organizer', description: 'Elegant 7-day supplement organizer with AM/PM compartments', price: 19.99, category: 'meal_kits', image_url: 'https://images.pexels.com/photos/3683098/pexels-photo-3683098.jpeg', in_stock: true },
    { id: 'nut-17', name: 'Portion Control Container Set', description: 'Color-coded containers for perfect portion sizes - 21 piece set', price: 34.99, category: 'meal_kits', image_url: 'https://images.pexels.com/photos/4397919/pexels-photo-4397919.jpeg', in_stock: true },
    { id: 'nut-18', name: 'Stylish Meal Prep Bag', description: 'Insulated bag with compartments for meal prep on the go', price: 54.99, category: 'meal_kits', image_url: 'https://images.pexels.com/photos/3622614/pexels-photo-3622614.jpeg', in_stock: true },
  ];

  useEffect(() => {
    setLoading(true);
    // Filter products based on category
    setTimeout(() => {
      if (selectedCategory === 'all') {
        setProducts(nutritionProducts);
      } else {
        setProducts(nutritionProducts.filter(p => p.category === selectedCategory));
      }
      setLoading(false);
    }, 300);
  }, [selectedCategory]);

  const handleAddToCart = async (product) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    try {
      // For nutrition products, we'd need to add them to the backend
      // For now, show a toast
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg"
            alt="ARTERYVAIL Nutrition Shop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#3D2314]/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">ARTERYVAIL Nutrition</p>
            <h1 className="font-serif text-5xl md:text-6xl text-[#F5F2EB] mb-6">
              Shop Wellness
            </h1>
            <p className="text-lg text-[#A3988F] leading-relaxed">
              Premium nutrition products, supplements, and accessories for your health journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-40 bg-[#1A1514]/95 backdrop-blur-lg border-b border-[#3D2314]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-full transition-all text-sm ${
                  selectedCategory === cat.id
                    ? 'bg-[#3D2314] text-[#E5D3B3] border border-[#5C3D2E]'
                    : 'bg-[#261E1D] text-[#A3988F] hover:bg-[#3D2314]/50 border border-transparent'
                }`}
                data-testid={`nutrition-category-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="loader" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <Leaf className="w-16 h-16 text-[#3D2314] mx-auto mb-6" />
              <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3">No Products Found</h3>
              <p className="text-[#A3988F]">Check back soon for new products.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="luxury-card rounded-xl overflow-hidden group"
                  style={{ borderColor: 'rgba(61, 35, 20, 0.3)' }}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#3D2314] text-[#E5D3B3] text-xs tracking-wider uppercase px-3 py-1 rounded-full">
                        {categories.find(c => c.id === product.category)?.name || product.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 bg-[#1A1514]">
                    <h3 className="font-serif text-lg text-[#F5F2EB] mb-2">{product.name}</h3>
                    <p className="text-[#A3988F] text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-semibold text-[#E5D3B3]">${product.price}</span>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="flex items-center space-x-2 text-sm text-[#F5F2EB] bg-[#3D2314] hover:bg-[#5C3D2E] px-4 py-2 rounded-full transition-colors"
                        data-testid={`add-nutrition-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        <span>Add</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Custom Meal Plans CTA */}
      <section className="section-padding bg-[#3D2314]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FFCBA4] mb-4">Personalized For You</p>
            <h2 className="font-serif text-4xl text-[#F5F2EB] mb-6">Need a Custom Meal Plan?</h2>
            <p className="text-[#A3988F] mb-8 max-w-2xl mx-auto">
              Our nutrition experts create personalized meal plans tailored to your health goals, dietary preferences, and lifestyle.
            </p>
            <Link to="/nutrition/meal-plans" className="btn-primary inline-flex items-center space-x-3" data-testid="nutrition-meal-plan-cta">
              <span>Request Custom Meal Plan</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default NutritionShopPage;
